import { Component, ChangeDetectionStrategy, input, output, inject, OnInit, signal, computed } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UnitType, UnitTypeUnit, ImageFile, NamingMode, PROPERTY_TYPE_DEFINITIONS, PropertyType } from '../../../models';
import { UnitReservationInfoDto } from '@app/modules/object/models';
import { ObjectTypeService } from '@app/modules/object/services/object-type.service';
import { GalleryUploadComponent, GalleryUploadState } from '../../shared/gallery-upload/gallery-upload.component';
import { AmenitySelectorComponent } from '../../shared/amenity-selector/amenity-selector.component';
import { AmenityApiService } from '../../../api/services';
import { WIZARD_LIMITS } from '../../../config/wizard-limits';

type UnitTypeSummaryItem = { label: string; value: string };

@Component({
  selector: 'app-unit-type-card',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    DragDropModule,
    InputText,
    InputNumber,
    Button,
    Tooltip,
    SelectModule,
    SelectButtonModule,
    TagModule,
    GalleryUploadComponent,
    AmenitySelectorComponent,
    TranslocoModule
  ],
  templateUrl: './unit-type-card.component.html',
  styleUrl: './unit-type-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnitTypeCardComponent implements OnInit {
  readonly unitType = input.required<UnitType>();
  readonly index = input.required<number>();
  readonly disabled = input<boolean>(false);
  readonly canRemove = input<boolean>(true);
  readonly validationErrors = input<string[]>([]);
  readonly startExpanded = input<boolean>(false);

  readonly unitChange = output<UnitType>();
  readonly remove = output<string>();
  readonly uploadStateChange = output<GalleryUploadState>();

  readonly limits = WIZARD_LIMITS;

  private readonly fb = inject(FormBuilder);
  private readonly amenityApiService = inject(AmenityApiService);
  private readonly objectTypeService = inject(ObjectTypeService);
  private readonly translocoService = inject(TranslocoService);


  readonly propertyTypes = computed<PropertyType[]>(() =>
    PROPERTY_TYPE_DEFINITIONS.map(pt => ({
      id: pt.id,
      name: this.translocoService.translate(pt.nameKey),
      icon: pt.icon
    }))
  );

  readonly namingModeOptions = computed(() => [
    { label: this.translocoService.translate('objects.wizard.unitTypes.namingModeAuto'), value: 'auto' as NamingMode },
    { label: this.translocoService.translate('objects.wizard.unitTypes.namingModeManual'), value: 'manual' as NamingMode }
  ]);

  /** Udogodnienia pobierane z API (type = 'unit') */
  readonly unitAmenities = this.amenityApiService.unitAmenities;
  readonly amenitiesLoading = this.amenityApiService.loading;
  readonly isExpanded = signal(false);
  namingHelpExpanded = false;

  readonly hasErrors = computed(() => this.validationErrors().length > 0);
  readonly errorsTooltip = computed(() => this.validationErrors().join('\n'));

  /** List of missing required fields for the Done button area */
  readonly missingFields = signal<string[]>([]);

  /** Stan uploadu galerii — blokuje walidację dopóki zdjęcia się wgrywają lub mają błąd */
  private readonly galleryUploadsSettled = signal(true);
  private readonly galleryPendingCount = signal(0);
  private readonly galleryFailedCount = signal(0);

  /** Summary items for collapsed card view */
  readonly summaryItems = computed<UnitTypeSummaryItem[]>(() => {
    const unit = this.unitType();
    const items: UnitTypeSummaryItem[] = [];

    const pt = PROPERTY_TYPE_DEFINITIONS.find(p => p.id === unit.propertyTypeId);
    if (pt) {
      items.push({ label: '', value: this.translocoService.translate(pt.nameKey) });
    }

    if (unit.quantity) {
      items.push({
        label: this.translocoService.translate('objects.wizard.unitTypes.quantity'),
        value: `${unit.quantity}`
      });
    }

    if (unit.maxOccupancy) {
      items.push({
        label: this.translocoService.translate('objects.wizard.unitTypes.maxOccupancy'),
        value: `${unit.maxOccupancy}`
      });
    }

    const imgCount = unit.images?.length || 0;
    if (imgCount > 0) {
      items.push({
        label: this.translocoService.translate('objects.wizard.unitTypes.photos'),
        value: `${imgCount}`
      });
    }

    return items;
  });

  /** Reservation info per unit (loaded from API in edit mode) */
  readonly unitReservations = signal<UnitReservationInfoDto[]>([]);

  /** Live preview of auto-generated names */
  readonly previewNames = signal<string[]>([]);

  /** Local copy of backend units, kept in sync with FormArray order */
  private readonly _localUnits = signal<UnitTypeUnit[]>([]);

  form!: FormGroup;

  get namingMode(): NamingMode {
    return this.form?.get('namingMode')?.value || 'auto';
  }

  get unitNamesArray(): FormArray<FormControl<string>> {
    return this.form.get('unitNames') as FormArray<FormControl<string>>;
  }

  ngOnInit(): void {
    if (this.startExpanded()) {
      this.isExpanded.set(true);
    }

    this.initForm();
    this.patchFormFromInput();
    this.updatePreview();
    this.updateMissingFields();

    // Zaladuj udogodnienia z API (cache w serwisie)
    this.amenityApiService.loadAmenities().subscribe();

    // Zaladuj dane rezerwacji per unit (tylko w trybie edycji)
    this.loadUnitReservations();
  }

  getReservationInfo(unitIndex: number): UnitReservationInfoDto | null {
    const units = this._localUnits();
    if (!units.length || !units[unitIndex]) return null;
    const unitId = units[unitIndex].id;
    return this.unitReservations().find(r => r.unitId === unitId) ?? null;
  }

  private loadUnitReservations(): void {
    const backendTypeId = parseInt(this.unitType().id, 10);
    if (!isNaN(backendTypeId) && this.unitType().units?.length) {
      this.objectTypeService.getUnitReservations(backendTypeId)
        .subscribe(data => this.unitReservations.set(data));
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      propertyTypeId: [null, [Validators.required]],
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      description: [''],
      quantity: [1, [Validators.required, Validators.min(1), Validators.max(100)]],
      maxOccupancy: [2, [Validators.required, Validators.min(1), Validators.max(20)]],
      namingMode: ['auto' as NamingMode],
      namingBase: ['', [Validators.required]],
      unitNames: this.fb.array<FormControl<string>>([]),
      squareMeters: [null],
      bedrooms: [null],
      beds: [null],
      bathrooms: [null],
      images: [[]],
      amenityIds: [[]]
    });

    this.form.get('namingMode')!.valueChanges.subscribe((mode: NamingMode) => {
      this.onNamingModeChange(mode);
    });

    this.form.get('namingBase')!.valueChanges.subscribe(() => {
      this.updatePreview();
    });

    this.form.get('quantity')!.valueChanges.subscribe((qty: number) => {
      this.onQuantityChange(qty);
    });

    this.form.valueChanges.subscribe(() => {
      this.emitChange();
      this.updateMissingFields();
    });
  }

  private patchFormFromInput(): void {
    const unit = this.unitType();
    const mode = unit.namingMode || 'auto';

    // Extract base name from namingPattern (remove " {n}" suffix)
    let namingBase = '';
    if (unit.namingPattern) {
      namingBase = unit.namingPattern.replace(/\s*\{n\}\s*$/, '');
    }

    this.form.patchValue({
      propertyTypeId: unit.propertyTypeId,
      name: unit.name,
      description: unit.description,
      quantity: unit.quantity,
      maxOccupancy: unit.maxOccupancy,
      namingMode: mode,
      namingBase,
      squareMeters: unit.squareMeters,
      bedrooms: unit.bedrooms,
      beds: unit.beds,
      bathrooms: unit.bathrooms,
      images: unit.images,
      amenityIds: unit.amenityIds
    }, { emitEvent: false });

    // Sync unitNames FormArray and local units copy
    this.syncUnitNamesArray(unit.quantity, unit.unitNames || []);
    this._localUnits.set([...(unit.units || [])]);

    // Update validators based on initial mode
    this.updateNamingValidators(mode);
  }

  private onNamingModeChange(mode: NamingMode): void {
    this.updateNamingValidators(mode);
    if (mode === 'auto') {
      this.updatePreview();
    } else {
      // In manual mode, ensure at least 1 row exists and sync quantity
      if (this.unitNamesArray.length === 0) {
        this.unitNamesArray.push(new FormControl<string>('', { nonNullable: true }));
      }
      this.syncQuantityFromManualNames();
    }
  }

  private updateNamingValidators(mode: NamingMode): void {
    const namingBaseCtrl = this.form.get('namingBase')!;
    if (mode === 'auto') {
      namingBaseCtrl.setValidators([Validators.required]);
    } else {
      namingBaseCtrl.clearValidators();
    }
    namingBaseCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private onQuantityChange(qty: number): void {
    if (!qty || qty < 1) return;

    // Only sync names array in auto mode; in manual mode quantity is derived
    if (this.namingMode === 'auto') {
      const currentNames = this.unitNamesArray.controls.map(c => c.value);
      this.syncUnitNamesArray(qty, currentNames);
      this.updatePreview();
    }
  }

  private syncUnitNamesArray(quantity: number, existingNames: string[]): void {
    const arr = this.unitNamesArray;
    const current = arr.length;

    if (current < quantity) {
      for (let i = current; i < quantity; i++) {
        arr.push(new FormControl<string>(existingNames[i] || '', { nonNullable: true }));
      }
    } else if (current > quantity) {
      for (let i = current - 1; i >= quantity; i--) {
        arr.removeAt(i);
      }
    }
  }

  private updatePreview(): void {
    const base = this.form?.get('namingBase')?.value?.trim() || '';
    const qty = this.form?.get('quantity')?.value || 0;
    if (!base || qty < 1) {
      this.previewNames.set([]);
      return;
    }
    const names: string[] = [];
    for (let i = 1; i <= qty; i++) {
      names.push(`${base} ${i}`);
    }
    this.previewNames.set(names);
  }

  private emitChange(): void {
    const value = this.form.getRawValue();
    const mode: NamingMode = value.namingMode || 'auto';

    let unitNames: string[] = [];

    if (mode === 'auto') {
      const base = value.namingBase?.trim() || '';
      const qty = value.quantity || 0;
      if (base && qty > 0) {
        for (let i = 1; i <= qty; i++) {
          unitNames.push(`${base} ${i}`);
        }
      }
    } else {
      unitNames = (value.unitNames || []).map((n: string) => n?.trim() || '');
    }

    const localUnits = this._localUnits();

    const unit: UnitType = {
      ...this.unitType(),
      propertyTypeId: value.propertyTypeId ?? null,
      name: value.name?.trim() || '',
      description: '',
      quantity: value.quantity,
      maxOccupancy: value.maxOccupancy,
      namingMode: mode,
      namingPattern: mode === 'auto' ? `${value.namingBase?.trim() || ''} {n}` : '',
      unitNames,
      units: localUnits.length ? localUnits : this.unitType().units,
      squareMeters: value.squareMeters,
      bedrooms: value.bedrooms,
      beds: value.beds,
      bathrooms: value.bathrooms,
      images: value.images,
      amenityIds: value.amenityIds || []
    };
    this.unitChange.emit(unit);
  }

  onImagesChange(images: ImageFile[]): void {
    this.form.patchValue({ images });
    this.updateMissingFields();
  }

  /** Reaguje na zmianę stanu uploadu w galerii i propaguje go w górę do kroku. */
  onGalleryUploadState(state: GalleryUploadState): void {
    this.galleryUploadsSettled.set(state.settled);
    this.galleryPendingCount.set(state.pending);
    this.galleryFailedCount.set(state.failed);
    this.updateMissingFields();
    this.uploadStateChange.emit(state);
  }

  onAmenitiesChange(amenityIds: number[]): void {
    this.form.patchValue({ amenityIds });
  }

  onRemove(): void {
    this.remove.emit(this.unitType().id);
  }

  toggleExpanded(): void {
    this.isExpanded.update(v => !v);
  }

  onDone(): void {
    if (!this.isValid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isExpanded.set(false);
  }

  onCancel(): void {
    this.isExpanded.set(false);
  }

  // --- Manual mode: add / remove / reorder units ---

  canRemoveUnit(index: number): boolean {
    if (this.unitNamesArray.length <= 1) return false;
    const info = this.getReservationInfo(index);
    return !info || info.futureReservationCount === 0;
  }

  addUnit(): void {
    this.unitNamesArray.push(new FormControl<string>('', { nonNullable: true }));
    this.syncQuantityFromManualNames();
  }

  removeUnit(index: number): void {
    if (!this.canRemoveUnit(index)) return;
    this.unitNamesArray.removeAt(index, { emitEvent: false });

    // Keep local units in sync
    const units = [...this._localUnits()];
    if (index < units.length) {
      units.splice(index, 1);
      this._localUnits.set(units);
    }

    this.syncQuantityFromManualNames();
  }

  onUnitDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const arr = this.unitNamesArray;
    const ctrl = arr.at(event.previousIndex);
    arr.removeAt(event.previousIndex, { emitEvent: false });
    arr.insert(event.currentIndex, ctrl, { emitEvent: false });

    // Keep local units in sync with FormArray order
    const units = [...this._localUnits()];
    if (units.length > 0) {
      moveItemInArray(units, event.previousIndex, event.currentIndex);
      this._localUnits.set(units);
    }

    this.emitChange();
  }

  private syncQuantityFromManualNames(): void {
    const count = this.unitNamesArray.length;
    this.form.get('quantity')!.setValue(count, { emitEvent: false });
    this.emitChange();
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName);
    if (!control || !control.errors || (!control.touched && !control.dirty)) return null;

    const errors = control.errors;
    if (errors['required']) return this.translocoService.translate('objects.wizard.validation.requiredShort');
    if (errors['minlength']) return this.translocoService.translate('objects.wizard.validation.minlengthShort', { length: errors['minlength'].requiredLength });
    if (errors['maxlength']) return this.translocoService.translate('objects.wizard.validation.maxlengthShort', { length: errors['maxlength'].requiredLength });
    if (errors['min']) return this.translocoService.translate('objects.wizard.validation.minValueShort', { min: errors['min'].min });
    if (errors['max']) return this.translocoService.translate('objects.wizard.validation.maxValueShort', { max: errors['max'].max });

    return this.translocoService.translate('objects.wizard.validation.invalidValue');
  }

  get isValid(): boolean {
    const images: ImageFile[] = this.form.get('images')?.value || [];
    const hasMinImages = images.length >= 1;
    // Tylko potwierdzone zdjęcia trafiają do form.images; dodatkowo wymagamy,
    // by nic się nie wgrywało i nie było błędów uploadu.
    return this.form.valid && hasMinImages && this.galleryUploadsSettled();
  }

  private updateMissingFields(): void {
    if (!this.form) {
      this.missingFields.set([]);
      return;
    }

    const missing: string[] = [];
    const t = (key: string) => this.translocoService.translate(key);

    const images: ImageFile[] = this.form.get('images')?.value || [];
    if (images.length < 1) {
      missing.push(t('objects.wizard.unitTypes.missingPhotos'));
    } else if (this.galleryFailedCount() > 0) {
      missing.push(t('objects.wizard.unitTypes.photosUploadFailed'));
    } else if (this.galleryPendingCount() > 0) {
      missing.push(t('objects.wizard.unitTypes.photosStillUploading'));
    }

    if (!this.form.get('propertyTypeId')?.value) {
      missing.push(t('objects.wizard.unitTypes.missingPropertyType'));
    }

    const name = this.form.get('name')?.value?.trim();
    if (!name || name.length < 3) {
      missing.push(t('objects.wizard.unitTypes.missingName'));
    }

    if (this.namingMode === 'auto') {
      const base = this.form.get('namingBase')?.value?.trim();
      if (!base) {
        missing.push(t('objects.wizard.unitTypes.missingNamingBase'));
      }
    }

    this.missingFields.set(missing);
  }
}
